import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import * as constantsViewer from '../../constants';
import { useSpring, a } from 'react-spring/three';
import { DoorFrontPane, DoorHoleShadow } from '../pieces/DoorFrontPane';
import {
  DoorKnop,
  DoorGlassHole,
  DoorSmallButton,
  DoorBigButton,
  DoorSlat,
} from '../pieces/DoorHandle';
import {
  onPointerDown,
  onPointerUp,
  manageOnClickComponent,
} from '../../helpers';
import { useDispatch } from 'react-redux';
import { applyPaintColor } from 'containers/PaintPanel/actions';

export function Door({
  door,
  cabinetId,
  shelfLevel,
  configuratorMode,
  shelfHeight,
  cabinetWidth,
  cabinetDepth,
  unselected = false,
}) {
  const group = useRef();
  const dispatch = useDispatch();

  const doorHandlesType = door.doorHandleType;
  let doorHandle3D = null;
  let dPosition = door.position;
  let rotation = [0, 0, 0];
  if (door.position == 'BL') {
    // Simulate front door when back door, back door with its handle will be moved together to back
    dPosition = 'FL';
    rotation = [0, Math.PI, 0];
  }
  if (door.position == 'BR') {
    dPosition = 'FR';
    rotation = [0, -Math.PI, 0];
  }

  const [spring, set] = useSpring(() => ({
    position: [0, 0, 0],
    immediate: !constantsViewer.REACT_SPRING_DOORS_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_DOORS_ANIMATIONS_CONF,
  }));

  let position = [0, 0, 0];
  useEffect(() => {
    let self = group.current;
    if (self) {
      var box = new THREE.Box3().setFromObject(self);
      let size = new THREE.Vector3();
      box.getSize(size);
      set({
        position: getPosition(door, size),
      });
    }
  }, [door.open]);

  switch (doorHandlesType) {
    case 'C':
      doorHandle3D = (
        <DoorKnop
          door={door}
          doorHandle={door.handle}
          dPosition={dPosition}
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          unselected={unselected}
        />
      );
      break;
    case 'G':
      doorHandle3D = (
        <DoorGlassHole
          door={door}
          doorHandle={door.handle}
          dPosition={dPosition}
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          unselected={unselected}
        />
      );
      break;
    case 'B':
      doorHandle3D = (
        <DoorSmallButton
          door={door}
          doorHandle={door.handle}
          dPosition={dPosition}
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          unselected={unselected}
        />
      );
      break;
    case 'H':
      doorHandle3D = (
        <DoorBigButton
          door={door}
          doorHandle={door.handle}
          dPosition={dPosition}
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          unselected={unselected}
        />
      );
      break;
    case 'S':
      doorHandle3D = (
        <>
          <DoorSlat
            door={door}
            dPosition={dPosition}
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            shelfHeight={shelfHeight}
            unselected={unselected}
          />
          {!door.doorIsInside ? (
            <DoorSlat
              door={door}
              dPosition="C"
              cabinetWidth={cabinetWidth}
              cabinetDepth={cabinetDepth}
              shelfHeight={shelfHeight}
              unselected={unselected}
            />
          ) : null}
        </>
      );
      break;

    default:
      break;
  }

  let doorHandleShadow = null;
  if (door && doorHandlesType === null && door.base.hole > 0)
    doorHandleShadow = (
      <DoorHoleShadow
        door={door}
        dPosition={dPosition}
        shelfHeight={shelfHeight}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
    );

  return (
    <a.group
      ref={group}
      onPointerDown={e => onPointerDown(e)}
      onPointerUp={e =>
        onPointerUp(e, e => {
          manageOnClickComponent(
            e,
            unselected,
            door,
            cabinetId,
            dispatch,
            configuratorMode,
            shelfLevel,
          );
        })
      }
      position={spring.position}
      rotation={rotation}
    >
      <DoorFrontPane
        door={door}
        dPosition={dPosition}
        shelfHeight={shelfHeight}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        unselected={unselected}
      />
      {doorHandle3D}
      {doorHandleShadow}
    </a.group>
  );
}

function getPosition(door, size) {
  const handleWidth = 110;
  switch (door.position) {
    case 'FL':
    case 'BR':
      return [(size.x - handleWidth) * (door.open / 100), 0, 0];
    case 'FR':
    case 'BL':
      return [-(size.x - handleWidth) * (door.open / 100), 0, 0];
    default:
      break;
  }
}
